import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  apiFetchSharedMailboxAutoMarkAsRead,
  SHARED_MAILBOX_AUTO_MARK_AS_READ_SETTING_KEY,
} from '../../settings/api';
import { ESettingScope, Setting } from '../../../models/Settings';
import { Project } from '../../../models/Project';
import { ProjectId } from '../../../models/Types';
import {
  getProjectAutoMarkAsReadByProjectId,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import { setProjectAutoMarkAsRead } from '../actions/projects/autoMarkAsRead';

export const SHARED_MAILBOX_AUTO_MARK_AS_READ_DEFAULT = true;

export const sharedMailboxAutoMarkAsReadQueryKeys = {
  all: [`setting:${SHARED_MAILBOX_AUTO_MARK_AS_READ_SETTING_KEY}`] as const,
  byProject: (projectId: ProjectId) =>
    [...sharedMailboxAutoMarkAsReadQueryKeys.all, projectId] as const,
  byProjects: (projectIds: ProjectId[]) =>
    [
      ...sharedMailboxAutoMarkAsReadQueryKeys.all,
      'projects',
      ...projectIds,
    ] as const,
};

const isRealProjectId = (projectId?: ProjectId) =>
  !!projectId && projectId !== 'me' && projectId !== 'favorites';

export const isSharedMailboxAutoMarkAsReadEnabled = (
  setting: Setting | null | undefined
): boolean => {
  if (!setting || setting.couldNotBeLoaded) {
    return SHARED_MAILBOX_AUTO_MARK_AS_READ_DEFAULT;
  }
  try {
    const isEnabled = JSON.parse(setting.jsonValue || '{}')?.isEnabled;
    return typeof isEnabled === 'boolean'
      ? isEnabled
      : SHARED_MAILBOX_AUTO_MARK_AS_READ_DEFAULT;
  } catch {
    return SHARED_MAILBOX_AUTO_MARK_AS_READ_DEFAULT;
  }
};

/**
 * Reads the effective setting `SharedMailbox_AutoMarkAsRead` of a project
 * (inheritance project -> office -> global). Without a value the default is used.
 * The last known value is persisted in redux and used while the setting is loading,
 * so `isLoading` is only true if no value is known at all.
 */
export const useSharedMailboxAutoMarkAsRead = (
  projectId?: ProjectId
): {
  setting: Setting | null;
  isEnabled: boolean;
  isLoading: boolean;
  isSettingLoading: boolean;
} => {
  const dispatch = useDispatch();
  const enabled = isRealProjectId(projectId);

  const storedIsEnabled = useSelector<RootReducerState, boolean | undefined>(
    (state) => getProjectAutoMarkAsReadByProjectId(state)[projectId]
  );

  const { data, isLoading } = useQuery({
    queryKey: sharedMailboxAutoMarkAsReadQueryKeys.byProject(projectId),
    queryFn: async () => {
      const { result, data } = await apiFetchSharedMailboxAutoMarkAsRead([
        projectId,
      ]);
      if (!result.ok) {
        throw new Error(`Failed to fetch setting: ${result.status}`);
      }
      return (
        data?.find(
          (setting) =>
            setting.key === SHARED_MAILBOX_AUTO_MARK_AS_READ_SETTING_KEY
        ) ?? null
      );
    },
    staleTime: 1000 * 60 * 5,
    enabled,
  });

  const isEnabled =
    data !== undefined
      ? isSharedMailboxAutoMarkAsReadEnabled(data)
      : (storedIsEnabled ?? SHARED_MAILBOX_AUTO_MARK_AS_READ_DEFAULT);

  useEffect(() => {
    if (enabled && data !== undefined && isEnabled !== storedIsEnabled) {
      dispatch(setProjectAutoMarkAsRead({ [projectId]: isEnabled }));
    }
  }, [enabled, data, isEnabled, storedIsEnabled, projectId, dispatch]);

  return {
    setting: data ?? null,
    isEnabled,
    isLoading: enabled && isLoading && storedIsEnabled === undefined,
    isSettingLoading: enabled && isLoading,
  };
};

/**
 * Reads the effective setting `SharedMailbox_AutoMarkAsRead` of several projects in one request.
 * Entries are mapped back via the inheritance chain project -> office (internalOfficeId) -> global.
 * While loading, the last known values persisted in redux are returned.
 */
export const useSharedMailboxAutoMarkAsReadByProjects = (
  projects: Pick<Project, 'projectId' | 'internalOfficeId'>[]
): { isEnabledByProjectId: Record<ProjectId, boolean>; isLoading: boolean } => {
  const dispatch = useDispatch();

  const storedIsEnabledByProjectId = useSelector(
    getProjectAutoMarkAsReadByProjectId
  );

  const projectIds = useMemo(
    () =>
      projects
        .map(({ projectId }) => projectId)
        .filter(isRealProjectId)
        .sort(),
    [projects]
  );

  const { data: settings, isLoading } = useQuery({
    queryKey: sharedMailboxAutoMarkAsReadQueryKeys.byProjects(projectIds),
    queryFn: async () => {
      const { result, data } =
        await apiFetchSharedMailboxAutoMarkAsRead(projectIds);
      if (!result.ok) {
        throw new Error(`Failed to fetch settings: ${result.status}`);
      }
      return (
        data?.filter(
          (setting) =>
            setting.key === SHARED_MAILBOX_AUTO_MARK_AS_READ_SETTING_KEY
        ) ?? []
      );
    },
    staleTime: 1000 * 60 * 5,
    enabled: projectIds.length > 0,
  });

  const fetchedIsEnabledByProjectId = useMemo(() => {
    if (!settings) return undefined;
    const findSetting = (scope: Setting['scope'], scopeId?: string) =>
      settings.find(
        (setting) =>
          setting.scope === scope && (!scopeId || setting.scopeId === scopeId)
      );
    return projects.reduce<Record<ProjectId, boolean>>(
      (map, { projectId, internalOfficeId }) => {
        const setting =
          findSetting(ESettingScope.PROJECT, projectId) ??
          (internalOfficeId
            ? findSetting(ESettingScope.OFFICE, internalOfficeId)
            : undefined) ??
          findSetting(ESettingScope.GLOBAL);
        map[projectId] = isSharedMailboxAutoMarkAsReadEnabled(setting);
        return map;
      },
      {}
    );
  }, [projects, settings]);

  useEffect(() => {
    if (
      fetchedIsEnabledByProjectId &&
      Object.entries(fetchedIsEnabledByProjectId).some(
        ([projectId, isEnabled]) =>
          storedIsEnabledByProjectId[projectId] !== isEnabled
      )
    ) {
      dispatch(setProjectAutoMarkAsRead(fetchedIsEnabledByProjectId));
    }
  }, [fetchedIsEnabledByProjectId, storedIsEnabledByProjectId, dispatch]);

  return {
    isEnabledByProjectId:
      fetchedIsEnabledByProjectId ?? storedIsEnabledByProjectId,
    isLoading: projectIds.length > 0 && isLoading,
  };
};

export default useSharedMailboxAutoMarkAsRead;
